import { isNativeAppWebview } from 'core/utils/webapp';
import { v4 as uuidv4 } from 'uuid';

import packageJson from '../../../package.json';
import { getDeviceContext } from './analyticsDevice';
import { buildEvent, utf8ByteLength } from './analyticsEvent';
import { redactRouteSecrets } from './analyticsRoute';

import type { AnalyticsEvent, AnalyticsFlatRecord, AnalyticsProducer, AnalyticsUser } from './analyticsEvent';

export const EVENTS_ENDPOINT = 'v3/events';
// Máximo de eventos por petición del contrato de POST /api/v3/events (CAM-16).
export const MAX_BATCH_SIZE = 50;
export const FLUSH_DELAY_MS = 5000;
// Fetch limita a 64 KiB la suma de los cuerpos keepalive en vuelo, y la cuota es de toda la página:
// posthog-js (1.369.2) también pide keepalive en sus POST de menos de 52428 bytes. La mitad reduce el
// riesgo de quitarle el sitio al último envío de PostHog, pero no lo evita si ese envío pasa de 32 KiB.
export const MAX_KEEPALIVE_BODY_BYTES = 32 * 1024;
// api-v3 reenvía a la ingesta dentro de la petición, sin cola propia: ante un fallo transitorio (503)
// solo puede reintentar el cliente. Esperas, tope y descarte, de Alejandro en CAM-16 (2026-10-21).
export const RETRY_DELAYS_MS = [5000, 15000, 45000, 120000, 300000];
export const MAX_RETRY_BUFFER_EVENTS = 500;
const RETRY_JITTER = 0.2;

export type AnalyticsHeaders = Record<string, string>;
export type AnalyticsIdentity = { language?: string | null; schoolGuid?: string | null };
export type AnalyticsConfig = { baseUrl: string; getHeaders: () => AnalyticsHeaders };
export type SessionAuth = { authToken?: string | null; loginContext?: string | null };

type DeliveryResponse = { headers?: { get: (name: string) => string | null } | null; status: number };
type FetchLike = (url: string, init: RequestInit) => Promise<DeliveryResponse | null | undefined>;
type ListenerTarget = {
  addEventListener: (type: string, listener: EventListenerOrEventListenerObject) => void;
  removeEventListener: (type: string, listener: EventListenerOrEventListenerObject) => void;
};
type VisibilityTarget = ListenerTarget & { visibilityState?: DocumentVisibilityState };
type ReadyListener = (isReady: boolean) => void;

export type AnalyticsClientDeps = {
  documentTarget?: VisibilityTarget | null;
  fetch?: FetchLike;
  getContext?: () => AnalyticsFlatRecord;
  now?: () => Date;
  producer?: AnalyticsProducer;
  random?: () => number;
  uuid?: () => string;
  windowTarget?: ListenerTarget | null;
};

export type AnalyticsClient = {
  configure: (config: AnalyticsConfig) => void;
  flush: () => Promise<void>;
  identify: (identity: AnalyticsIdentity | null) => void;
  isReady: () => boolean;
  subscribe: (listener: ReadyListener) => () => void;
  teardown: () => void;
  track: (eventType: string, payload?: unknown) => boolean;
};

type FlushOptions = { isLeavingPage: boolean };
// order: el puesto del lote en el buffer de reintentos, por antigüedad, desde que falla la primera vez.
type Batch = { events: AnalyticsEvent[]; headers: AnalyticsHeaders; order?: number; retries: number };
type PendingRetry = Batch & { dueAt: number; order: number };
type DeliveryResult = { retryAfterMs: number | null; shouldRetry: boolean };

const BATCH_BODY_OVERHEAD_BYTES = utf8ByteLength(JSON.stringify({ events: [] }));

// fetch y no axios (CAM-16): el interceptor de setupAxios manda a /logout ante un 401 o un 403 de
// sesión, y un fallo de v3/events no puede cerrar la sesión del usuario. Se resuelve al enviar, no al
// crear el cliente, para que un test pueda sustituir globalThis.fetch.
function defaultFetch(url: string, init: RequestInit): Promise<DeliveryResponse> {
  return globalThis.fetch(url, init);
}

function defaultContext(): AnalyticsFlatRecord {
  // Ruta sin query string ni hash, que pueden llevar el token de sesión (URLs de ficheros del storage), y
  // con el patrón en vez del token en las rutas que lo llevan en el path (`/auth/login/:token`…).
  return { ...getDeviceContext(), is_app: isNativeAppWebview(), route: redactRouteSecrets(globalThis.location?.pathname ?? '') };
}

// crypto y no Math.random, que Sonar marca como hotspot de seguridad aunque aquí solo reparte las esperas.
function defaultRandom(): number {
  const values = globalThis.crypto?.getRandomValues?.(new Uint32Array(1));
  return values ? values[0] / 2 ** 32 : 0.5;
}

// Retry-After admite segundos o una fecha HTTP. Ojo: no es una cabecera visible por CORS, así que desde
// localhost contra dev1 no llega salvo que api-v3 la exponga; en el mismo origen, sí.
function parseRetryAfter(value: string | null | undefined, nowMs: number): number | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  if (/^\d+$/.test(trimmed)) return Number(trimmed) * 1000;
  const date = Date.parse(trimmed);
  return Number.isNaN(date) ? null : Math.max(0, date - nowMs);
}

function toUser(identity: AnalyticsIdentity): AnalyticsUser {
  const user: AnalyticsUser = {};
  if (identity.schoolGuid) user.school_guid = identity.schoolGuid;
  if (identity.language) user.language = identity.language;
  return user;
}

/** Las mismas cabeceras de sesión que pone setupAxios (`core/utils/axios.js`): el token va sin `Bearer`. */
export function sessionHeaders(auth: SessionAuth | null | undefined): AnalyticsHeaders {
  const headers: AnalyticsHeaders = {};
  if (auth?.authToken) headers.Authorization = auth.authToken;
  if (auth?.loginContext) headers.login_context = auth.loginContext;
  return headers;
}

function headersKey(headers: AnalyticsHeaders): string {
  return JSON.stringify(
    Object.keys(headers)
      .sort((first, second) => first.localeCompare(second))
      .map((name) => [name, headers[name]])
  );
}

function joinUrl(baseUrl: string, path: string): string {
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  return `${base}/${path}`;
}

function withSentAt(events: AnalyticsEvent[], sentAt: string): AnalyticsEvent[] {
  return events.map((event) => ({ ...event, sent_at: sentAt }));
}

function splitBySize(events: AnalyticsEvent[], maxBytes: number): AnalyticsEvent[][] {
  const chunks: AnalyticsEvent[][] = [];
  let chunk: AnalyticsEvent[] = [];
  let chunkBytes = BATCH_BODY_OVERHEAD_BYTES;

  events.forEach((event) => {
    const eventBytes = utf8ByteLength(JSON.stringify(event)) + 1;
    if (chunk.length > 0 && chunkBytes + eventBytes >= maxBytes) {
      chunks.push(chunk);
      chunk = [];
      chunkBytes = BATCH_BODY_OVERHEAD_BYTES;
    }
    chunk.push(event);
    chunkBytes += eventBytes;
  });
  if (chunk.length > 0) chunks.push(chunk);
  return chunks;
}

export function createAnalyticsClient(deps: AnalyticsClientDeps = {}): AnalyticsClient {
  const {
    documentTarget = globalThis.document ?? null,
    fetch: fetchImpl = defaultFetch,
    getContext = defaultContext,
    now = (): Date => new Date(),
    producer = { app: 'frontoffice', platform: 'web', version: packageJson.version },
    random = defaultRandom,
    uuid = uuidv4,
    windowTarget = globalThis.window ?? null,
  } = deps;

  const sessionId = uuid();
  const readyListeners = new Set<ReadyListener>();
  let config: AnalyticsConfig | null = null;
  let user: AnalyticsUser | null = null;
  let queue: AnalyticsEvent[] = [];
  let queueHeaders: AnalyticsHeaders = {};
  let flushTimer: ReturnType<typeof setTimeout> | null = null;
  let retryBuffer: PendingRetry[] = [];
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let isRetryInFlight = false;
  let retryOrder = 0;
  let wasReady = false;

  function isReady(): boolean {
    return config !== null && user !== null;
  }

  function notifyIfReadyChanged(): void {
    const ready = isReady();
    if (ready === wasReady) return;
    wasReady = ready;
    readyListeners.forEach((listener) => listener(ready));
  }

  function clearFlushTimer(): void {
    if (flushTimer === null) return;
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  function clearRetryTimer(): void {
    if (retryTimer === null) return;
    clearTimeout(retryTimer);
    retryTimer = null;
  }

  // async corre en el acto hasta el primer await, así que fetch sale dentro del propio pagehide; y un fetch que
  // lanza (sin soporte, cuerpo inválido) o que falla acaba en el catch: nunca rompe a quien emite el evento.
  // Se reintenta un 5xx o un error de red. Un 4xx no (el lote no va a cambiar) y un 202 tampoco, aunque
  // traiga `discarded`: son eventos inválidos.
  async function send(url: string, headers: AnalyticsHeaders, body: string, keepalive: boolean): Promise<DeliveryResult> {
    try {
      const response = await fetchImpl(url, {
        body,
        headers: { ...headers, 'Content-Type': 'application/json' },
        keepalive,
        method: 'POST',
      });
      const shouldRetry = (response?.status ?? 0) >= 500;
      return { retryAfterMs: shouldRetry ? parseRetryAfter(response?.headers?.get('Retry-After'), now().getTime()) : null, shouldRetry };
    } catch {
      return { retryAfterMs: null, shouldRetry: true };
    }
  }

  function countBufferedEvents(): number {
    return retryBuffer.reduce((total, pending) => total + pending.events.length, 0);
  }

  // Si el buffer se llena, se pierden los eventos más antiguos, aunque sea partiendo un lote.
  function trimRetryBuffer(): void {
    let excess = countBufferedEvents() - MAX_RETRY_BUFFER_EVENTS;
    while (excess > 0 && retryBuffer.length > 0) {
      const [oldest] = retryBuffer;
      if (oldest.events.length <= excess) {
        excess -= oldest.events.length;
        retryBuffer.shift();
      } else {
        oldest.events = oldest.events.slice(excess);
        excess = 0;
      }
    }
  }

  function nextRetryDelay(retries: number, retryAfterMs: number | null): number {
    if (retryAfterMs !== null) return retryAfterMs;
    return Math.round(RETRY_DELAYS_MS[retries] * (1 - RETRY_JITTER + 2 * RETRY_JITTER * random()));
  }

  function bufferForRetry(batch: Batch, retryAfterMs: number | null): void {
    // Sin config, el cliente se ha desmontado (teardown); con todos los reintentos hechos, se descarta.
    if (config === null || batch.retries >= RETRY_DELAYS_MS.length) return;

    const order = batch.order ?? retryOrder++;
    const pending: PendingRetry = { ...batch, dueAt: now().getTime() + nextRetryDelay(batch.retries, retryAfterMs), order };
    const insertAt = retryBuffer.findIndex((other) => other.order > order);
    if (insertAt === -1) retryBuffer.push(pending);
    else retryBuffer.splice(insertAt, 0, pending);
    trimRetryBuffer();
    // El nuevo puede vencer antes que el que tenía el timer
    clearRetryTimer();
    scheduleRetry();
  }

  function deliver(url: string, batch: Batch, body: string, keepalive: boolean): Promise<void> {
    return send(url, batch.headers, body, keepalive).then((result) => {
      if (result.shouldRetry) bufferForRetry(batch, result.retryAfterMs);
    });
  }

  function runRetry(): void {
    retryTimer = null;
    if (config === null || retryBuffer.length === 0) return;

    const dueIndex = retryBuffer.reduce((earliest, pending, index) => (pending.dueAt < retryBuffer[earliest].dueAt ? index : earliest), 0);
    const [pending] = retryBuffer.splice(dueIndex, 1);
    const body = JSON.stringify({ events: withSentAt(pending.events, now().toISOString()) });
    isRetryInFlight = true;
    deliver(joinUrl(config.baseUrl, EVENTS_ENDPOINT), { ...pending, retries: pending.retries + 1 }, body, false).finally(() => {
      isRetryInFlight = false;
      scheduleRetry();
    });
  }

  function scheduleRetry(): void {
    if (retryTimer !== null || isRetryInFlight || retryBuffer.length === 0) return;
    const nextDueAt = Math.min(...retryBuffer.map((pending) => pending.dueAt));
    retryTimer = setTimeout(runRetry, Math.max(0, nextDueAt - now().getTime()));
  }

  function flushQueue({ isLeavingPage }: FlushOptions): Promise<void> {
    clearFlushTimer();
    if (config === null) return Promise.resolve();

    const url = joinUrl(config.baseUrl, EVENTS_ENDPOINT);
    const sentAt = now().toISOString();
    const batches: Array<Batch> = [];
    if (isLeavingPage) {
      // Lo pendiente de reintento no espera a su turno: al salir, el timer ya no llegaría a dispararse.
      clearRetryTimer();
      batches.push(...retryBuffer.map((pending) => ({ ...pending, retries: pending.retries + 1 })));
      retryBuffer = [];
    }
    if (queue.length > 0) batches.push({ events: queue, headers: queueHeaders, retries: 0 });
    queue = [];
    queueHeaders = {};
    if (batches.length === 0) return Promise.resolve();

    if (!isLeavingPage) return deliver(url, batches[0], JSON.stringify({ events: withSentAt(batches[0].events, sentAt) }), false);

    // Los trozos que no caben en el cupo keepalive salen sin él: si la pestaña solo se ha ocultado,
    // llegan igual; si se está cerrando, el navegador rechazaría el keepalive de todos modos.
    let keepaliveBudget = MAX_KEEPALIVE_BODY_BYTES;
    const requests = batches.flatMap((batch) =>
      splitBySize(withSentAt(batch.events, sentAt), MAX_KEEPALIVE_BODY_BYTES).map((events) => {
        const body = JSON.stringify({ events });
        const bodyBytes = utf8ByteLength(body);
        const keepalive = bodyBytes <= keepaliveBudget;
        if (keepalive) keepaliveBudget -= bodyBytes;
        return deliver(url, { ...batch, events }, body, keepalive);
      })
    );
    return Promise.all(requests).then(() => undefined);
  }

  function handlePageHide(): void {
    flushQueue({ isLeavingPage: true });
  }

  function handleVisibilityChange(): void {
    if (documentTarget?.visibilityState === 'hidden') flushQueue({ isLeavingPage: true });
  }

  // addEventListener ignora la misma función repetida: configure puede llamarse más de una vez.
  function attachListeners(): void {
    windowTarget?.addEventListener('pagehide', handlePageHide);
    documentTarget?.addEventListener('visibilitychange', handleVisibilityChange);
  }

  function detachListeners(): void {
    windowTarget?.removeEventListener('pagehide', handlePageHide);
    documentTarget?.removeEventListener('visibilitychange', handleVisibilityChange);
  }

  function configure(nextConfig: AnalyticsConfig): void {
    config = nextConfig;
    attachListeners();
    notifyIfReadyChanged();
  }

  function identify(identity: AnalyticsIdentity | null): void {
    if (identity === null) {
      // Con las cabeceras capturadas al encolar, porque el logout ya ha borrado el token del store, y
      // con keepalive, porque Logout redirige enseguida (50 ms en app) y el navegador abortaría el fetch.
      flushQueue({ isLeavingPage: true });
      user = null;
    } else {
      user = toUser(identity);
    }
    notifyIfReadyChanged();
  }

  function track(eventType: string, payload: unknown = {}): boolean {
    const currentConfig = config;
    const currentUser = user;
    if (currentConfig === null || currentUser === null) return false;

    const headers = currentConfig.getHeaders();
    // Entre el reducer Logout y el identify(null) de InitsContainer el store ya no tiene token:
    // api-v3 respondería 401 a ese lote.
    if (!headers.Authorization) return false;

    const event = buildEvent(eventType, payload, {
      context: getContext(),
      eventId: uuid(),
      occurredOn: now().toISOString(),
      producer,
      sessionId,
      user: currentUser,
    });
    if (event === null) return false;

    // Un lote viaja con un único token: si la sesión ha cambiado, lo pendiente sale con el anterior.
    if (queue.length > 0 && headersKey(headers) !== headersKey(queueHeaders)) flushQueue({ isLeavingPage: false });

    queue.push(event);
    queueHeaders = headers;
    if (queue.length >= MAX_BATCH_SIZE) flushQueue({ isLeavingPage: false });
    else flushTimer ??= setTimeout(() => flushQueue({ isLeavingPage: false }), FLUSH_DELAY_MS);
    return true;
  }

  function subscribe(listener: ReadyListener): () => void {
    readyListeners.add(listener);
    return () => {
      readyListeners.delete(listener);
    };
  }

  function teardown(): void {
    detachListeners();
    clearFlushTimer();
    clearRetryTimer();
    retryBuffer = [];
    isRetryInFlight = false;
    queue = [];
    queueHeaders = {};
    config = null;
    user = null;
    notifyIfReadyChanged();
    readyListeners.clear();
  }

  return {
    configure,
    flush: () => flushQueue({ isLeavingPage: false }),
    identify,
    isReady,
    subscribe,
    teardown,
    track,
  };
}

export const analytics = createAnalyticsClient();
